import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { Box, LevelBox } from '../modal/box';
import ItemSelect from '../components/item_select';
import { AppDispatch, RootState } from '../app/store';
import { useDispatch, useSelector } from 'react-redux';
import { getAllBoxes, resetGetAllBoxesStatus, resetUpdateLevelBoxStatus, updateLevelBox } from '../features/boxSlice';
import { getImageSource } from '../utils/utils';
import Modal from '../components/modal';
import { toast } from 'react-toastify';

// Styled Components
const Container = styled.div`
  padding: 30px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: 24px;
  color: #333;
  text-align: center;
  margin-bottom: 30px;
`;

const FieldWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 25px;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 16px;
  color: #555;
  margin-right: 10px;
`;

const BoxDetails = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  margin-top: 20px;

  img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    margin-right: 20px;
  }

  .details {
    display: flex;
    flex-direction: column;

    div {
      font-size: 14px;
      color: #333;
      margin-bottom: 5px;
    }

    strong {
      font-weight: 600;
      color: #444;
    }
  }
`;

const SaveButton = styled.button`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: bold;
  color: #fff;
  background-color: #007bff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #0056b3;
  }

  &:active {
    background-color: #004085;
  }
`;

const levelOptions = Array.from({ length: 11 }, (_, i) => ({
  value: i,
  label: `Level ${i}`,
}));

interface ManageLevelBoxPageProps {
  isOpen: boolean;
  onClose: () => void;
}

const ManageLevelBoxPage: React.FC<ManageLevelBoxPageProps> = ({ isOpen, onClose }) => {
  const dispatch: AppDispatch = useDispatch();
  const { selectedLevelBox, allBoxes, getAllBoxesStatus, updateLevelBoxError, updateLevelBoxStatus } = useSelector((state: RootState) => state.boxes);

  const [selectedLevel, setSelectedLevel] = useState<number>(selectedLevelBox?.level || 0);
  const [selectedBox, setSelectedBox] = useState<Box | null>(selectedLevelBox?.box ?? null);

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllBoxes());
    }
  }, [dispatch, isOpen]);

  useEffect(() => {
    // Set the selectedBox only when the boxes are successfully fetched
    if (getAllBoxesStatus === 'succeeded') {
      const initialBox = allBoxes.find(box => box.name === selectedLevelBox?.box?.name) || null;
      setSelectedBox(initialBox);
      dispatch(resetGetAllBoxesStatus());
    }
  }, [getAllBoxesStatus, allBoxes, selectedLevelBox, dispatch]);


  useEffect(() => {
    // Set the selectedBox only when the boxes are successfully fetched
    if (getAllBoxesStatus === 'succeeded') {
      dispatch(resetUpdateLevelBoxStatus());


    }
  }, [resetUpdateLevelBoxStatus, dispatch]);




  // Handle updateBox status changes
  useEffect(() => {
    if (updateLevelBoxStatus === "succeeded") {
      onClose();
      toast("Level box updated", {
        autoClose: 3000, // Set the duration in milliseconds (3 seconds in this example)
        onClose: () => { },
      });

      dispatch(resetUpdateLevelBoxStatus());
    } else if (updateLevelBoxStatus === "failed") {
      toast(`Error updating level box: ${updateLevelBoxError}`, {
        autoClose: 3000, // Set the duration in milliseconds (3 seconds in this example)
        onClose: () => { },
      });

      dispatch(resetUpdateLevelBoxStatus());
    }
  }, [resetUpdateLevelBoxStatus, updateLevelBoxError, updateLevelBoxStatus, dispatch]);




  const handleBoxSelect = (index: number, selectedBoxName: string | null) => {
    const box = allBoxes.find(b => b.name === selectedBoxName) || null;
    setSelectedBox(box);
  };

  const handleLevelSelect = (selected: any) => {
    setSelectedLevel(selected.value);
  };

  const handleSaveOrUpdate = () => {
    if(selectedBox){
        const boxId = selectedBox.id!; 
        const vipLevel = selectedLevel.toString();
        dispatch(updateLevelBox({ boxId: boxId, vipLevel: vipLevel }));
    }
  };

  const isUpdateMode = Boolean(selectedBox);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Level Box">
      <Container>
        <FieldWrapper>
          <Label>Level:</Label>
          <Select
            value={levelOptions.find(option => option.value === selectedLevel)}
            onChange={handleLevelSelect}
            options={levelOptions}
            placeholder="Select level..."
            styles={{
              container: base => ({ ...base, flex: 1 }),
            }}
          />
        </FieldWrapper>

        <FieldWrapper>
          <Label>Box:</Label>
          <ItemSelect
            items={allBoxes}
            selectedItemName={selectedBox ? selectedBox.name : null}
            index={0}
            handleItemSelect={handleBoxSelect}
            mapToOption={(box: Box) => ({
              value: box.name,
              label: box.name +" $"+ box.final_value,
              imageUrl: box.picture ?? "",
            })}
          />
        </FieldWrapper>

        {selectedBox && (
          <BoxDetails>
            <img src={getImageSource(selectedBox.picture)} alt={selectedBox.name} />
            <div className="details">
              <div>
                <strong>Name:</strong> {selectedBox.name}
              </div>
              <div>
                <strong>Price:</strong> ${selectedBox.final_value}
              </div>
            </div>
          </BoxDetails>
        )}

        <SaveButton onClick={handleSaveOrUpdate}>
          {isUpdateMode ? 'Update' : 'Save'}
        </SaveButton>
      </Container>
    </Modal>
  );
};

export default ManageLevelBoxPage;
